/**
 * Shared Chart.js tooltip config for dashboard doughnut/pie and bar charts.
 * Use getDashboardChartTooltipWithPercentOfTotal() for charts that show "label: value (X% of total)".
 */

export const DASHBOARD_CHART_TOOLTIP_STYLE = {
  backgroundColor: '#4A5568',
  titleColor: '#ffffff',
  bodyColor: '#ffffff',
  borderColor: '#4A5568',
  borderWidth: 0,
  cornerRadius: 8,
  displayColors: false
} as const;

/**
 * Tooltip label that shows current value and percentage of dataset total.
 * Works for both doughnut (context.parsed as number) and bar (context.parsed.y).
 */
export function getDashboardTooltipLabelPercentOfTotal(
  context: { label: string; parsed: number | { y: number }; dataset: { data: number[] } },
  useLocaleString = false
): string {
  const currentValue =
    typeof context.parsed === 'object' && context.parsed !== null && 'y' in context.parsed
      ? (context.parsed as { y: number }).y
      : (context.parsed as number);
  const total = context.dataset.data.reduce((sum: number, value: number) => sum + value, 0);
  const percentageOfTotal = total > 0 ? (currentValue / total) * 100 : 0;
  const valueText = useLocaleString ? currentValue.toLocaleString() : String(currentValue);
  return `${context.label}: ${valueText} (${percentageOfTotal.toFixed(1)}%)`;
}

/**
 * Full tooltip config with empty title and percentage-of-total label callback.
 */
export function getDashboardChartTooltipWithPercentOfTotal(useLocaleString = false) {
  return {
    ...DASHBOARD_CHART_TOOLTIP_STYLE,
    callbacks: {
      title: () => '',
      label: (context: any) => getDashboardTooltipLabelPercentOfTotal(context, useLocaleString)
    }
  };
}

/**
 * Shared tooltip shell for line/bar charts with empty title and custom label formatter.
 */
export function getDashboardChartTooltipWithCustomLabel(
  labelFormatter: (context: any) => string
) {
  return {
    ...DASHBOARD_CHART_TOOLTIP_STYLE,
    callbacks: {
      title: () => '',
      label: (context: any) => labelFormatter(context)
    }
  };
}

/**
 * Base options for dashboard doughnut/pie charts: no legend, shared tooltip, responsive.
 */
export function getDashboardDoughnutChartOptions(useLocaleString = false) {
  return {
    plugins: {
      legend: { display: false },
      tooltip: getDashboardChartTooltipWithPercentOfTotal(useLocaleString)
    },
    responsive: true,
    maintainAspectRatio: false
  };
}

/**
 * Tooltip label callback that shows current value and percentage change from previous data point.
 * Used by newVsReturningTrendOptions and monthlyAcquisitionOptions in dashboard.
 */
export function getDashboardTooltipLabelWithPercentChange(context: any): string {
  const currentValue = context.parsed.y;
  const dataIndex = context.dataIndex;
  const dataset = context.dataset;
  if (dataIndex > 0 && dataset.data[dataIndex - 1] !== undefined) {
    const previousValue = dataset.data[dataIndex - 1];
    const change = currentValue - previousValue;
    const percentageChange = previousValue === 0 ? (currentValue > 0 ? 100 : 0) : (change / previousValue) * 100;
    if (percentageChange > 0) return `${context.dataset.label}: ${currentValue} (+${percentageChange.toFixed(1)}%)`;
    if (percentageChange < 0) return `${context.dataset.label}: ${currentValue} (${percentageChange.toFixed(1)}%)`;
    return `${context.dataset.label}: ${currentValue} (0%)`;
  }
  return `${context.dataset.label}: ${currentValue}`;
}

/**
 * Tooltip label with percentage change and configurable value format (e.g. $ prefix, locale string).
 * Used by revenue trend and avg revenue per team charts.
 */
export function getDashboardTooltipLabelWithPercentChangeAndFormat(
  context: any,
  opts: { formatValue?: (v: number) => string } = {}
): string {
  const currentValue = context.parsed.y;
  const dataIndex = context.dataIndex;
  const dataset = context.dataset;
  const timeLabel = context.label;
  const format = opts.formatValue ?? ((v: number) => String(v));
  if (dataIndex > 0 && dataset.data[dataIndex - 1] !== undefined) {
    const previousValue = dataset.data[dataIndex - 1];
    const change = currentValue - previousValue;
    const percentageChange = previousValue === 0 ? (currentValue > 0 ? 100 : 0) : (change / previousValue) * 100;
    if (percentageChange > 0) return `${timeLabel}: ${format(currentValue)} (+${percentageChange.toFixed(1)}%)`;
    if (percentageChange < 0) return `${timeLabel}: ${format(currentValue)} (${percentageChange.toFixed(1)}%)`;
    return `${timeLabel}: ${format(currentValue)} (0%)`;
  }
  return `${timeLabel}: ${format(currentValue)}`;
}

const DASHBOARD_LINE_X_SCALE = {
  grid: { display: false },
  border: { display: false },
  ticks: { color: '#555963', font: { family: 'Inter', size: 12, weight: 500 } }
};

/**
 * Line chart options with percent-change tooltip and shared scales.
 * Used for newVsReturningTrend and monthlyAcquisition charts; set scales.y.max in component after.
 */
export function getDashboardLineChartOptionsWithPercentChange(
  yPosition: 'left' | 'right',
  dynamicMax: number,
  opts?: { yPadding?: number; yBorderDash?: number[]; barBorderRadius?: number }
) {
  const yScale: any = {
    position: yPosition,
    grid: { color: 'rgba(0, 0, 26, 0.15)', drawTicks: false },
    border: { display: false },
    ticks: {
      color: '#555963',
      font: { family: 'Inter', size: 12, weight: 500 },
      padding: opts?.yPadding ?? 10,
      stepSize: 400
    },
    min: 0,
    max: dynamicMax
  };
  if (opts?.yBorderDash) yScale.border.dash = opts.yBorderDash;
  return {
    plugins: {
      legend: { display: false },
      tooltip: getDashboardChartTooltipWithCustomLabel(getDashboardTooltipLabelWithPercentChange)
    },
    scales: { x: DASHBOARD_LINE_X_SCALE, y: yScale },
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: true, mode: 'nearest' as const },
    ...(opts?.barBorderRadius != null && { elements: { bar: { borderRadius: opts.barBorderRadius } } })
  };
}

/**
 * Shared scales config for dashboard line charts (revenue, avg revenue, etc.).
 * Optionally pass a y-axis tick callback (e.g. format as $ or k).
 */
export function getDashboardLineChartScales(
  dynamicMax: number,
  opts?: { yTickCallback?: (value: any) => string; yPadding?: number; maxTicksLimit?: number; stepSize?: number }
) {
  return {
    x: DASHBOARD_LINE_X_SCALE,
    y: {
      position: 'left' as const,
      grid: { color: 'rgba(0, 0, 26, 0.15)', drawTicks: false },
      border: { display: false },
      ticks: {
        color: '#555963',
        font: { family: 'Inter', size: 12, weight: 500 },
        padding: opts?.yPadding ?? 10,
        maxTicksLimit: opts?.maxTicksLimit ?? 10,
        ...(opts?.yTickCallback && { callback: opts.yTickCallback }),
        ...(opts?.stepSize != null && { stepSize: opts.stepSize })
      },
      min: 0,
      max: dynamicMax
    }
  };
}

/**
 * Line chart options with custom tooltip label, shared scales, responsive, and index interaction.
 * Use for revenue trend, avg revenue per team, campaign performance trend, etc.
 */
export function getDashboardLineChartOptionsWithCustomTooltip(
  tooltipLabelFn: (context: any) => string,
  dynamicMax: number,
  scaleOpts?: { yTickCallback?: (value: any) => string; yPadding?: number; stepSize?: number }
) {
  return {
    plugins: {
      legend: { display: false },
      tooltip: getDashboardChartTooltipWithCustomLabel(tooltipLabelFn)
    },
    scales: getDashboardLineChartScales(dynamicMax, scaleOpts),
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' as const }
  };
}

/**
 * Washes-by-hour (or similar) line chart options: tooltip shows "label: value washes (±X%)", shared scales, stepSize 5.
 * Dashboard uses this for washesByHourOptions; loadWashCountTrend updates scales.y.max dynamically.
 */
export function getDashboardWashesByHourChartOptions(initialMax = 25) {
  return getDashboardLineChartOptionsWithCustomTooltip(
    (context: any) => getDashboardTooltipLabelWithPercentChangeAndFormat(context, { formatValue: (v) => v + ' washes' }),
    initialMax,
    { yPadding: 10, stepSize: 5 }
  );
}

/**
 * Shared point style for dashboard line chart datasets (pointRadius, pointHover*, etc.).
 * Use with spread: { ...getDashboardLineChartPointStyle(borderColor), ...otherDatasetProps }.
 */
export function getDashboardLineChartPointStyle(
  borderColor: string,
  options?: { pointRadius?: number }
) {
  const pointRadius = options?.pointRadius ?? 0;
  return {
    pointBackgroundColor: borderColor,
    pointBorderColor: borderColor,
    pointRadius,
    pointHoverRadius: 6,
    pointHoverBackgroundColor: borderColor,
    pointHoverBorderColor: '#ffffff',
    pointHoverBorderWidth: 2
  };
}

/**
 * Factory for a standard fill-under dashboard line chart dataset.
 * Sets fill, tension 0.4, and applies getDashboardLineChartPointStyle automatically.
 */
export function createDashboardLineDataset(
  label: string,
  data: any[],
  borderColor: string,
  backgroundColor: string,
  borderWidth = 1,
  pointStyleOptions?: { pointRadius?: number }
) {
  return {
    label,
    data,
    borderColor,
    backgroundColor,
    borderWidth,
    fill: true,
    tension: 0.4,
    ...getDashboardLineChartPointStyle(borderColor, pointStyleOptions)
  };
}
